<template>
  <el-dialog
    title="分类对比"
    :visible.sync="dialogVisible"
    width="1200px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
    top="5vh"
  >
    <!-- 选择销售组织 -->
    <div class="org-select-bar">
      <div class="org-select-item">
        <span class="label">源销售组织：</span>
        <el-select v-model="sourceOrgId" placeholder="请选择源销售组织" filterable style="width: 250px">
          <el-option
            v-for="org in saleOrgList"
            :key="org.id"
            :label="org.name"
            :value="org.id"
          />
        </el-select>
      </div>
      <div class="org-select-item">
        <span class="label">目标销售组织：</span>
        <el-select v-model="targetOrgId" placeholder="请选择目标销售组织" filterable style="width: 250px">
          <el-option
            v-for="org in saleOrgList"
            :key="org.id"
            :label="org.name"
            :value="org.id"
          />
        </el-select>
      </div>
      <el-button type="primary" icon="el-icon-sort" @click="handleCompare" :loading="loading">开始对比</el-button>
    </div>

    <!-- 图例说明 -->
    <div class="legend-bar" v-if="hasCompareResult">
      <span class="legend-item">
        <span class="legend-dot same"></span>完全一致
      </span>
      <span class="legend-item">
        <span class="legend-dot unique"></span>独有节点
      </span>
      <span class="legend-item">
        <span class="legend-dot conflict"></span>冲突节点
      </span>
    </div>

    <!-- 对比结果 -->
    <div class="compare-container" v-loading="loading">
      <template v-if="hasCompareResult">
        <!-- 左侧树：源组织 -->
        <div class="tree-panel">
          <div class="tree-header">
            <span class="tree-title">{{ sourceOrgName }}</span>
          </div>
          <div class="tree-content">
            <el-tree
              :data="sourceTree"
              :props="treeProps"
              node-key="id"
              default-expand-all
              :expand-on-click-node="false"
            >
              <span class="compare-tree-node" slot-scope="{ node, data }">
                <span :class="['node-label', getNodeClass(data.compareStatus)]">
                  <i :class="data.children && data.children.length > 0 ? 'el-icon-folder' : 'el-icon-document'"></i>
                  {{ data.name }}
                  <span class="node-number">({{ data.number }})</span>
                </span>
              </span>
            </el-tree>
            <el-empty v-if="sourceTree.length === 0" description="暂无分类数据"></el-empty>
          </div>
        </div>

        <!-- 右侧树：目标组织 -->
        <div class="tree-panel">
          <div class="tree-header">
            <span class="tree-title">{{ targetOrgName }}</span>
          </div>
          <div class="tree-content">
            <el-tree
              :data="targetTree"
              :props="treeProps"
              node-key="id"
              default-expand-all
              :expand-on-click-node="false"
            >
              <span class="compare-tree-node" slot-scope="{ node, data }">
                <span :class="['node-label', getNodeClass(data.compareStatus)]">
                  <i :class="data.children && data.children.length > 0 ? 'el-icon-folder' : 'el-icon-document'"></i>
                  {{ data.name }}
                  <span class="node-number">({{ data.number }})</span>
                </span>
              </span>
            </el-tree>
            <el-empty v-if="targetTree.length === 0" description="暂无分类数据"></el-empty>
          </div>
        </div>
      </template>

      <el-empty v-if="!hasCompareResult && !loading" description="请选择两个销售组织进行对比"></el-empty>
    </div>

    <!-- 冲突列表 -->
    <div class="conflict-section" v-if="conflicts.length > 0">
      <div class="conflict-header">
        <span class="conflict-title">冲突列表 ({{ conflicts.length }})</span>
      </div>
      <el-table :data="conflicts" border stripe max-height="200px">
        <el-table-column prop="conflictType" label="冲突类型" width="180">
          <template slot-scope="scope">
            <el-tag :type="scope.row.conflictType === 'SAME_NUMBER_DIFF_NAME' ? 'warning' : 'danger'">
              {{ getConflictTypeName(scope.row.conflictType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="源组织分类" min-width="200">
          <template slot-scope="scope">
            <span>{{ scope.row.sourceNode.name }} ({{ scope.row.sourceNode.number }})</span>
          </template>
        </el-table-column>
        <el-table-column label="目标组织分类" min-width="200">
          <template slot-scope="scope">
            <span>{{ scope.row.targetNode.name }} ({{ scope.row.targetNode.number }})</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div slot="footer">
      <el-button @click="handleClose">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { compareSaleCategory } from '@/api/system/sale_category'

export default {
  name: 'CategoryCompare',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    saleOrgList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      sourceOrgId: '',
      targetOrgId: '',
      loading: false,
      sourceTree: [],
      targetTree: [],
      conflicts: [],
      treeProps: {
        children: 'children',
        label: 'name'
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    hasCompareResult() {
      return this.sourceTree.length > 0 || this.targetTree.length > 0
    },
    sourceOrgName() {
      const org = this.saleOrgList.find(o => o.id === this.sourceOrgId)
      return org ? org.name : '源组织'
    },
    targetOrgName() {
      const org = this.saleOrgList.find(o => o.id === this.targetOrgId)
      return org ? org.name : '目标组织'
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetData()
      }
    }
  },
  methods: {
    resetData() {
      this.sourceOrgId = ''
      this.targetOrgId = ''
      this.sourceTree = []
      this.targetTree = []
      this.conflicts = []
    },

    async handleCompare() {
      if (!this.sourceOrgId) {
        this.$message.warning('请选择源销售组织')
        return
      }
      if (!this.targetOrgId) {
        this.$message.warning('请选择目标销售组织')
        return
      }
      if (this.sourceOrgId === this.targetOrgId) {
        this.$message.warning('请选择两个不同的销售组织')
        return
      }

      this.loading = true
      try {
        const res = await compareSaleCategory({
          sourceSaleOrgId: this.sourceOrgId,
          targetSaleOrgId: this.targetOrgId
        })
        if (res.code === 200 && res.data) {
          this.sourceTree = res.data.sourceTree || []
          this.targetTree = res.data.targetTree || []
          this.conflicts = res.data.conflicts || []
        } else {
          this.$message.error(res.msg || '对比失败')
        }
      } catch (error) {
        console.error('对比分类失败:', error)
        this.$message.error('对比分类失败')
      } finally {
        this.loading = false
      }
    },

    getNodeClass(compareStatus) {
      const classMap = {
        'SAME': 'status-same',
        'UNIQUE': 'status-unique',
        'CONFLICT': 'status-conflict'
      }
      return classMap[compareStatus] || ''
    },

    getConflictTypeName(type) {
      const nameMap = {
        'SAME_NUMBER_DIFF_NAME': '同编码不同名',
        'SAME_NAME_DIFF_NUMBER': '同名不同编码'
      }
      return nameMap[type] || type
    },

    handleClose() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.org-select-bar {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 15px;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.org-select-item {
  display: flex;
  align-items: center;
}

.org-select-item .label {
  margin-right: 8px;
  font-weight: 500;
}

.legend-bar {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  padding: 10px 15px;
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #606266;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
}

.legend-dot.same {
  background-color: #909399;
}

.legend-dot.unique {
  background-color: #67c23a;
}

.legend-dot.conflict {
  background-color: #e6a23c;
}

.compare-container {
  display: flex;
  gap: 15px;
  min-height: 400px;
}

.tree-panel {
  flex: 1;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
}

.tree-header {
  padding: 12px 15px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}

.tree-title {
  font-weight: 600;
  color: #303133;
}

.tree-content {
  flex: 1;
  padding: 10px;
  overflow: auto;
  max-height: 350px;
}

.compare-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  font-size: 13px;
}

.node-label {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 6px;
  border-radius: 3px;
}

.node-label i {
  color: #909399;
}

.node-number {
  color: #909399;
  font-size: 12px;
}

.status-same {
  background-color: transparent;
}

.status-unique {
  background-color: #f0f9eb;
  color: #67c23a;
}

.status-unique i,
.status-unique .node-number {
  color: #67c23a;
}

.status-conflict {
  background-color: #fdf6ec;
  color: #e6a23c;
}

.status-conflict i,
.status-conflict .node-number {
  color: #e6a23c;
}

.conflict-section {
  margin-top: 15px;
}

.conflict-header {
  margin-bottom: 10px;
}

.conflict-title {
  font-weight: 600;
  color: #303133;
}
</style>
